import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import AppNavigation from "../../components/navigation/appNavigation/AppNavigation";
import PricingModal from "../../pages/AccountInformation/PricingModals";
import QuillionzProUpgradeModal from "../../components/modals/QuillionzProUpgradeModal";
import { fetchUsersProfile } from "../../api/fetchUserProfile";
import Tier2UserManagement from "./Tier2UserManagement ";

const getFormattedMonth = (dateString) => {
  const date = new Date(dateString);
  const options = { month: "short", year: "numeric", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

const plans = {
  1: "Monthly",
  3: "Quarterly",
  12: "Annual",
};

const AccountInformation = () => {

  const navigate = useNavigate();

  const userEmail = localStorage.getItem("email");

  const userName = localStorage.getItem("username");

  const unlimitedUser = localStorage.getItem("userGPTKey") !== "LIMITED";

  const [isUnlimitedUser, setIsUnlimitedUser] = useState(unlimitedUser);

  const [subscriptionPlan, setSubscriptionPlan] = useState("");

  // Get TIERTYPE from localStorage
  const IsSubUser = localStorage.getItem("IsSubUser");

  // Show Tier2 management only when user is tier2 AND AddedUsers === 0 AND user is primary (IsSubUser === 0)
  const [showTier2UserManagement, setShowTier2UserManagement] = useState(() => {
    const TIERTYPE = localStorage.getItem("TIERTYPE");
    const IsSubUserStr = localStorage.getItem("IsSubUser");
    const added = parseInt(localStorage.getItem("AddedUsers") || "0", 10);
    const isTier2 =
      TIERTYPE === "tier2_monthly" || TIERTYPE === "tier2_annually";
    const isPrimary = Number(IsSubUserStr) === 0; // convert string -> number
    return isTier2 && added === 0 && isPrimary;
  });

  // Recompute visibility when AddedUsers changes elsewhere in the app
  useEffect(() => {
    const handler = () => {
      const TIERTYPE = localStorage.getItem("TIERTYPE");
      const added = parseInt(localStorage.getItem("AddedUsers") || "0", 10);
      const IsSubUserStr = localStorage.getItem("IsSubUser");
      const isTier2 =
        TIERTYPE === "tier2_monthly" || TIERTYPE === "tier2_annually";
      setShowTier2UserManagement(
        isTier2 && added === 0 && Number(IsSubUserStr) === 0
      );
    };
    window.addEventListener("tier2-updated", handler);
    return () => window.removeEventListener("tier2-updated", handler);
  }, []);

  // Effect hook to update subscription plan name based on the value
  useEffect(() => {
    async function getUsersProfile() {
      const { data, status } = await fetchUsersProfile(userEmail);
      if ([200, 201].includes(status)) {
        const {
          userData: { subscription },
        } = data;

        // Handle ExpiryDate validation
        const expiryDate = subscription.ExpiryDate;
        const timestamp = new Date(expiryDate).getTime();
        const formattedDate =
          !expiryDate || isNaN(timestamp) || timestamp === 0
            ? "NA"
            : getFormattedMonth(expiryDate);

        localStorage.setItem("planExpiryDate", formattedDate);

        // Store the plan validity for future use
        localStorage.setItem("subscriptionPlan", subscription.Plan_Validity);

        setSubscriptionPlan(plans[subscription.Plan_Validity] || "Free");
      }
    }

    // Always fetch user profile on mount
    getUsersProfile();
  }, [userEmail]); // Added userEmail as dependency

  const [showQuillionzProUpgradeModal, setShowQuillionzProUpgradeModal] =
    useState(false);


  // Function to Quillionz Pro Upgrade Modal
  const handleQuillionzProUpgradeModalClose = () => {
    setShowQuillionzProUpgradeModal(false);
  };

  // Function to change the password
  const handleChangePassword = () => {
    navigate("/changepassword");
  };
  const planExpiryDate = localStorage.getItem("planExpiryDate");

  const shouldShowPricingModal = IsSubUser === "0";

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <AppNavigation />
      <div className="flex flex-col lg:px-[1rem] flex-1 overflow-auto pb-8">
        <div className="flex mt-4">
          <h1 className="text-[#212529] text-base font-medium">
            Account Information
          </h1>
        </div>
        <div className="flex mt-[10px]">
          <h1 className="text-gray-500 text-xs font-medium ">
            Name: <span className="text-black">{userName}</span>
          </h1>
          <div className="w-px h-4 bg-gray-300 ml-[10px]"></div>
          <h1 className="text-gray-500 text-xs font-medium ml-[10px]">
            Email Address: <span className="text-black">{userEmail}</span>
          </h1>
          <div className="w-px h-4 bg-gray-300 ml-[10px]"></div>
          <h1 className="text-gray-500 text-xs font-medium ml-[10px]">
            <Tooltip
              title="Reset your password"
              arrow
              placement="top"
              TransitionComponent={Zoom}
              TransitionProps={{ timeout: 300 }}
            >
              <button
                onClick={handleChangePassword}
                className="rounded hover:opacity-90 font-black text-orange-500 text-xs pr-4"
              >
                Reset Password
              </button>
            </Tooltip>
          </h1>
        </div>

        <div className="flex flex-row gap-6 mt-[1rem]">
          {/* Account Type */}
          <div className="flex justify-between items-center mb-4 h-16 w-[100vh] bg-[#474785] rounded-lg cursor-pointer px-4">
            <div>
              <p className="text-[17px] text-white font-normal">
                Account Type:
              </p>
              <p className="text-yellow-400 font-medium text-xs">
                {isUnlimitedUser
                  ? "Unlimited"
                  : subscriptionPlan === "Free"
                  ? "Free"
                  : "Paid"}
              </p>
            </div>
            <i className="fa-solid fa-address-card text-[#9b9bff] text-4xl"></i>
          </div>

          {/* Payment Status */}
          <div className="flex justify-between items-center mb-4 h-16 w-[100vh] bg-[#474785] rounded-lg cursor-pointer px-4">
            <div>
              <p className="text-[17px] text-white font-normal">
                Payment Status:
              </p>
              <p className="font-medium text-xs text-yellow-400">
                {subscriptionPlan === "Free" ? "NA" : "Active"}
              </p>
            </div>
            <i className="fa-solid fa-credit-card text-[#9b9bff] text-4xl"></i>
          </div>

          {/* Subscription Type */}
          <div className="flex justify-between items-center mb-4 h-16 w-[100vh] bg-[#474785] rounded-lg cursor-pointer px-4">
            <div>
              <p className="text-[17px] text-white font-normal">
                Subscription Type:
              </p>
              <p className="font-medium text-xs text-yellow-400">
                {subscriptionPlan + ` Subscription`}
              </p>
            </div>
            <i className="fa-solid fa-money-check text-[#9b9bff] text-4xl"></i>
          </div>

          {/* Subscription Validity */}
          <div className="flex justify-between items-center mb-4 h-16 w-[100vh] bg-[#474785] rounded-lg cursor-pointer px-4">
            <div>
              <p className="text-[17px] text-white font-normal">
                Subscription Validity:
              </p>
              <p className="font-medium text-xs text-yellow-400">
                {planExpiryDate}
              </p>
            </div>
            <i className="fa-regular fa-calendar text-[#9b9bff] text-4xl"></i>
          </div>
        </div>

        {/* Only show pricing section if TIERTYPE exists */}
        {shouldShowPricingModal && (
          <>
            <div className="flex items-center justify-center my-[1rem]">
              <hr className="flex-grow border-gray-300 mx-4" />
              <span className="text-lg font-semibold text-gray-700 whitespace-nowrap">
                Flexible Plans & Pricings
              </span>
              <hr className="flex-grow border-gray-300 mx-4" />
            </div>

            {/* {Subscription PricingModal UI} */}
            <PricingModal />

            <hr className="my-[10px] mr-20 ml-20" />
          </>
        )}

        {/* Tier 2 User Management Component */}
        {showTier2UserManagement && <Tier2UserManagement />}
      </div>

      {/* Render the Quillionz Pro Upgrade Modal component */}
      {showQuillionzProUpgradeModal && (
        <QuillionzProUpgradeModal
          isOpen={showQuillionzProUpgradeModal}
          onClose={handleQuillionzProUpgradeModalClose}
        />
      )}
    </div>
  );
};

export default AccountInformation;
